import { travelDay } from '../../lib/calendar'
import { trackGeometry } from '../../lib/gpx'
import { routeGeometry, stageAnchors } from '../../lib/route'
import type { DiaryEntry, LatLon, Stage } from '../../lib/types'

/**
 * Übersetzt die Etappen in GeoJSON.
 *
 * Die echte Karte zeichnet die Route nicht als HTML über der Karte, sondern
 * als eigene Kartenebenen aus diesen beiden Sammlungen. Das ist der Grund,
 * warum die Umwandlung hier steht und nicht in der Komponente: sie ist die
 * einzige Stelle, an der aus Etappen Geometrie wird, sie enthält die ganze
 * Entscheidung gefahren neben geplant und berechnete Strasse gegen Luftlinie,
 * und ohne MapLibre und ohne DOM lässt sie sich prüfen.
 *
 * Die Typen sind absichtlich klein von Hand geschrieben statt aus @types/geojson
 * geholt: dieses Modul soll von der Kartenbibliothek nichts wissen. Sie sind
 * strukturell mit dem GeoJSON-Standard und damit mit MapLibre verträglich.
 */

/** Koordinate in GeoJSON-Reihenfolge: [Länge, Breite]. Umgekehrt zu LatLon. */
export type Position = [number, number]

export interface Feature<G, P> {
  type: 'Feature'
  geometry: G
  properties: P
}

export interface FeatureCollection<G, P> {
  type: 'FeatureCollection'
  features: Feature<G, P>[]
}

export interface LineString {
  type: 'LineString'
  coordinates: Position[]
}

export interface PointGeometry {
  type: 'Point'
  coordinates: Position
}

/**
 * Merkmale eines Streckenabschnitts.
 *
 * Hier stand einmal auch eine Tagesnummer. Gelesen hat sie niemand - der Stil
 * beschriftet mit label und filtert nach betont, routed und tracked -, und sie
 * war die Stelle in der Etappenliste, während die Beschriftung daneben den
 * Reisetag meint. Eine Zahl, die keiner braucht und die etwas anderes sagt als
 * ihr Nachbar, ist eine Falle für den Nächsten. Wer die Etappe braucht, hat
 * stageId.
 */
export interface RouteLineProps {
  stageId: string
  /**
   * Ob diese Linie kräftig gezeichnet wird - in Farbe und Strichstärke.
   *
   * Hier stand driven, und damit stand hier zweierlei. Ohne Vergleich heisst
   * die kräftige Linie „der Benutzer hat die Etappe im Tagebuch als gefahren
   * abgehakt“ - dieselbe Auskunft, die der Punkt am Etappenziel zeigt und die
   * in den Summen steckt (drivenKm, drivenCount in lib/trip.ts). Liegen
   * Planung und Aufzeichnung nebeneinander, heisst sie dagegen „das ist die
   * aufgezeichnete“, und zwar auch ohne Haken.
   *
   * Beides zugleich kann ein Feld nicht heissen. Weil an dieser Stelle
   * ausschliesslich die Zeichnung hängt, ist es das, was das Feld jetzt sagt:
   * welche der Linien vorn steht. Wer nach dem Tagebuch fragt, fragt driven -
   * und das gibt es nur noch dort, wo es genau das heisst.
   *
   * Warum die Aufzeichnung im Vergleich auch ohne Haken gewinnt: eine
   * aufgezeichnete Fahrt ist gefahren worden, ob jemand sie abhakt oder nicht.
   * Der Haken ist eine Planungsmarke, die Spur eine Messung, und von zwei
   * Linien derselben Etappe muss die gemessene die kräftige sein.
   */
  betont: boolean
  /**
   * Berechnete Strasse (true) oder Luftlinie (false). Steuert die Strichelung.
   *
   * Die Unterscheidung gehört in die Daten und nicht in den Stil: eine
   * Luftlinie ist eine Behauptung über den Weg, eine berechnete Strecke eine
   * Auskunft. Wer die Karte ansieht, soll das erkennen können, ohne die
   * Etappen durchzublättern.
   */
  routed: boolean
  /**
   * Aufgezeichneter Weg aus einer GPX-Datei.
   *
   * Der Track ist der vierte Fall und der wahrhaftigste: keine Behauptung wie
   * die Luftlinie und keine Auskunft eines Dienstes wie die berechnete Strasse,
   * sondern eine Messung. Er bekommt trotzdem keine dritte Unterscheidung
   * neben Farbe und Strichelung - die beiden bestehenden bleiben, wie sie sind,
   * und der Track ordnet sich in sie ein: er ist eine Strasse (routed), also
   * durchgezogen, und in aller Regel gefahren, also in der Farbe der Fahrt.
   *
   * Was ihn heraushebt, ist eine Hülle darunter: dieselbe Linie, breiter und
   * durchscheinend. Das liest sich als Unterstreichung dessen, was die Farbe
   * schon sagt, und nicht als eine weitere Behauptung - und wo keine
   * Aufzeichnung vorliegt, sieht die Karte aus wie zuvor.
   */
  tracked: boolean
  /** Beschriftung für Werkzeuge und Fehlersuche, nicht für die Karte. */
  label: string
}

/** Merkmale eines Etappenziels. */
export interface StagePointProps {
  stageId: string
  /**
   * Gefahren laut Tagebuch, also im Etappeneintrag abgehakt.
   *
   * Die eine Bedeutung, die dieses Wort im ganzen Programm hat - dieselbe wie
   * in den Summen (lib/trip.ts) und im Stempel an der Etappenkarte. Ein Punkt
   * ist ein Ort und keine Linie: hier gibt es nichts zu vergleichen und
   * deshalb auch keinen zweiten Anwärter auf die kräftige Farbe.
   */
  driven: boolean
  /** Ortsname, so wie er an den Punkt geschrieben wird. */
  label: string
}

/** Beides zusammen, so wie der Stil es als Quellen einhängt. */
export interface RouteGeoJson {
  lines: FeatureCollection<LineString, RouteLineProps>
  points: FeatureCollection<PointGeometry, StagePointProps>
}

/** Wandelt eine Koordinate aus dem Datenmodell in GeoJSON-Reihenfolge. */
export function toPosition([lat, lon]: readonly [number, number]): Position {
  return [lon, lat]
}

function isDriven(stage: Stage, diary: Record<string, DiaryEntry>): boolean {
  return diary[stage.id]?.done ?? false
}

/** Woher der Linienzug einer Etappe stammt. */
export type TraceKind =
  /** Aufgezeichnet, aus einer GPX-Datei im Tagebuch. */
  | 'track'
  /** Vom Routendienst berechnete Strasse. */
  | 'road'
  /** Luftlinie über die Punkte, die die Etappe vorgibt. */
  | 'air'

/** Der Weg einer Etappe im Datenmodell, also als [Breite, Länge]. */
export interface StageTrace {
  points: LatLon[]
  kind: TraceKind
}

/**
 * Die sichtbaren Wege einer Etappe, von hinten nach vorn.
 *
 * Die einzige Stelle mit dieser Entscheidung, und beide Karten holen sie sich
 * hier - die echte über die Sammlungen weiter unten, die schematische
 * unmittelbar (RouteMap.tsx). Liegen eine aktuelle berechnete Strecke und eine
 * Aufzeichnung vor, bleiben beide erhalten: zuerst die zurückhaltende Planung,
 * darüber die gefahrene Wirklichkeit. Ohne berechnete Strecke wäre eine
 * Luftlinie kein sinnvoller Vergleich; dann steht nur die Aufzeichnung da.
 *
 * Eine veraltete Strecke gilt dabei nicht als vorhanden: sie führt zu einem
 * Ziel, das nicht mehr das Ziel ist, und wäre auf der Karte von einer
 * aktuellen nicht zu unterscheiden. Für den Track gilt das ausdrücklich
 * nicht - er ist gefahren worden, gleichgültig, wohin die Etappe seither
 * geplant wird.
 *
 * Die Luftlinie läuft über die Zwischenziele und nicht an ihnen vorbei. Sie
 * behauptet ohnehin keine Strasse, aber sie soll das behaupten, was geplant
 * ist: wer über Zofingen, Olten und Aarau nach Hause fährt, hat keine Etappe
 * der Länge null, sondern vier Abschnitte - und ohne sie fiele eine Runde zu
 * einem einzigen Punkt zusammen.
 */
export function stageTraces(stage: Stage, entry?: DiaryEntry): StageTrace[] {
  const track = trackGeometry(entry)
  const geometry = routeGeometry(stage)

  if (track && geometry) {
    return [
      { points: geometry, kind: 'road' },
      { points: track, kind: 'track' },
    ]
  }
  if (track) return [{ points: track, kind: 'track' }]
  if (geometry) return [{ points: geometry, kind: 'road' }]

  return [{ points: stageAnchors(stage), kind: 'air' }]
}

/**
 * Alles, was in den Kartenausschnitt einer Etappe gehört.
 *
 * Die Punkte der Etappe selbst und dazu jede Linie, die für sie gezeichnet
 * wird - die berechnete Strasse, die Aufzeichnung, oder ersatzweise die
 * Luftlinie. Die Aufzeichnung zählt dabei mit und nicht nur nebenbei: sie kann
 * einen Umweg genommen haben, den weder die Planung noch die berechnete Strasse
 * kennt.
 *
 * Die Zwischenziele stehen ausdrücklich mit darin, auch wenn eine Strecke
 * vorliegt. Sie liegen zwar auf ihr - aber ein Ausschnitt, der sich allein auf
 * die Strecke verlässt, wäre bei einer Etappe ohne berechnete Strecke wieder
 * bei Start und Ziel, und eine Runde ergäbe einen Punkt.
 */
export function stageExtent(stage: Stage, entry?: DiaryEntry): LatLon[] {
  const punkte: LatLon[] = [...stageAnchors(stage)]
  for (const spur of stageTraces(stage, entry)) punkte.push(...spur.points)
  return punkte
}

/**
 * Der vorderste Weg einer Etappe.
 *
 * Ansichten, die nur eine Spur darstellen können, verwenden diese Auswahl.
 * Vergleichende Karten verwenden stageTraces.
 */
export function stageTrace(stage: Stage, entry?: DiaryEntry): StageTrace {
  return stageTraces(stage, entry).at(-1)!
}

/** Derselbe Weg in GeoJSON-Reihenfolge, samt der Merkmale für den Stil. */
export function stageLine(
  stage: Stage,
  entry?: DiaryEntry,
): { coordinates: Position[]; routed: boolean; tracked: boolean } {
  const { points, kind } = stageTrace(stage, entry)
  return {
    coordinates: points.map(toPosition),
    routed: kind !== 'air',
    tracked: kind === 'track',
  }
}

/**
 * Ein LineString je Etappe, bei einem Vergleich zwei.
 *
 * Bewusst getrennte Abschnitte und nicht eine durchgehende Linie: nur so kann
 * derselbe Datensatz gefahrene und geplante Abschnitte unterschiedlich
 * zeichnen. Beide Linien eines Vergleichs tragen dieselbe stageId; die
 * Auswahl bleibt damit eindeutig.
 *
 * Standtage, bei denen Start und Ziel zusammenfallen, bleiben drin. Sie
 * zeichnen nichts, weil die Linie keine Länge hat, aber sie fehlen dann auch
 * nicht in der Zählung.
 */
export function routeLines(
  stages: Stage[],
  diary: Record<string, DiaryEntry>,
  vergleiche = true,
): FeatureCollection<LineString, RouteLineProps> {
  return {
    type: 'FeatureCollection',
    features: stages.flatMap((stage) => {
      const eintrag = diary[stage.id]
      const spuren = vergleiche ? stageTraces(stage, eintrag) : [stageTrace(stage, eintrag)]
      const istVergleich = spuren.length > 1

      return spuren.map(({ points, kind }) => {
        const tracked = kind === 'track'
        const zusatz = istVergleich ? (tracked ? ' (Aufzeichnung)' : ' (Planung)') : ''
        return {
          type: 'Feature' as const,
          geometry: { type: 'LineString' as const, coordinates: points.map(toPosition) },
          properties: {
            stageId: stage.id,
            // Im Vergleich muss die Planung zurücktreten und die Aufzeichnung
            // nach vorn. Ohne Vergleich zeichnet weiterhin der Tagebuchhaken
            // die Etappe kräftig - siehe RouteLineProps.betont.
            betont: istVergleich ? tracked : isDriven(stage, diary),
            routed: kind !== 'air',
            tracked,
            label: `${stage.from} → ${stage.to}${zusatz}`,
          },
        }
      })
    }),
  }
}

/**
 * Ein Punkt je Etappenziel.
 *
 * Nur das Ziel, nicht der Start: der Start einer Etappe ist das Ziel der
 * vorigen, zwei Punkte je Etappe wären also fast immer derselbe Ort doppelt.
 * Die schematische Karte macht es genauso.
 */
export function stagePoints(
  stages: Stage[],
  diary: Record<string, DiaryEntry>,
): FeatureCollection<PointGeometry, StagePointProps> {
  return {
    type: 'FeatureCollection',
    features: stages.map((stage) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: toPosition(stage.c2) },
      properties: {
        stageId: stage.id,
        driven: isDriven(stage, diary),
        // Der Reisetag und nicht die Stelle in der Liste: dieselbe Zahl, die
        // im Kopf der Etappenseite und in der Übersichtsliste steht.
        label: `${String(travelDay(stages, stage))}. ${stage.to}`,
      },
    })),
  }
}

/** Linien und Punkte der ganzen Route in einem Rutsch. */
export function routeGeoJson(
  stages: Stage[],
  diary: Record<string, DiaryEntry>,
  vergleiche = true,
): RouteGeoJson {
  return { lines: routeLines(stages, diary, vergleiche), points: stagePoints(stages, diary) }
}

/**
 * Eine einzelne Etappe für die kleine Etappenkarte.
 *
 * Hier stehen beide Enden als Punkt, nicht nur das Ziel: die Etappenkarte zeigt
 * genau diese eine Fahrt, und ohne Startpunkt wäre nicht zu sehen, wo sie
 * beginnt. Der Tagebuchstand kommt als Wahrheitswert herein, weil für eine
 * Etappe die ganze Sammlung nicht gebraucht wird - der Eintrag selbst nur dann,
 * wenn der Aufrufer auch den aufgezeichneten Weg gezeichnet haben will.
 */
export function stageGeoJson(stage: Stage, driven: boolean, entry?: DiaryEntry): RouteGeoJson {
  const punkt = (coord: Stage['c1'], label: string): Feature<PointGeometry, StagePointProps> => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: toPosition(coord) },
    properties: { stageId: stage.id, driven, label },
  })

  const spuren = stageTraces(stage, entry)
  const istVergleich = spuren.length > 1

  return {
    lines: {
      type: 'FeatureCollection',
      features: spuren.map(({ points, kind }) => {
        const tracked = kind === 'track'
        const zusatz = istVergleich ? (tracked ? ' (Aufzeichnung)' : ' (Planung)') : ''
        return {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: points.map(toPosition) },
          properties: {
            stageId: stage.id,
            // Wie auf der grossen Karte tritt die Planung im Vergleich zurück,
            // während die aufgezeichnete Wirklichkeit kräftig darüberliegt.
            betont: istVergleich ? tracked : driven,
            routed: kind !== 'air',
            tracked,
            label: `${stage.from} → ${stage.to}${zusatz}`,
          },
        }
      }),
    },
    points: {
      type: 'FeatureCollection',
      features: [punkt(stage.c1, stage.from), punkt(stage.c2, stage.to)],
    },
  }
}
